import { useState, useCallback } from 'react';

/**
 * Custom hook for managing modal state
 * Provides a clean API for showing confirmation and alert modals
 */
export function useModal() {
  const [confirmationModal, setConfirmationModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'warning',
    confirmText: 'Confirm',
    cancelText: 'Cancel',
    showCancel: true,
    onConfirm: null
  });

  const [alertModal, setAlertModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'info',
    buttonText: 'OK'
  });

  const showConfirmation = useCallback((options) => {
    return new Promise((resolve) => {
      setConfirmationModal({
        isOpen: true,
        title: options.title || 'Confirm Action',
        message: options.message || 'Are you sure?',
        type: options.type || 'warning',
        confirmText: options.confirmText || 'Confirm',
        cancelText: options.cancelText || 'Cancel',
        showCancel: options.showCancel !== false,
        onConfirm: () => {
          resolve(true);
        },
        onCancel: () => {
          resolve(false);
        }
      });
    });
  }, []);

  const showAlert = useCallback((options) => {
    return new Promise((resolve) => {
      setAlertModal({
        isOpen: true,
        title: options.title || 'Notification',
        message: options.message || '',
        type: options.type || 'info',
        buttonText: options.buttonText || 'OK',
        onClose: () => resolve()
      });
    });
  }, []);

  const closeConfirmation = useCallback(() => {
    setConfirmationModal(prev => {
      if (prev.onCancel) {
        prev.onCancel();
      }
      return { ...prev, isOpen: false, onConfirm: null, onCancel: null };
    });
  }, []);

  const closeAlert = useCallback(() => {
    setAlertModal(prev => {
      if (prev.onClose) {
        prev.onClose();
      }
      return { ...prev, isOpen: false, onClose: null };
    });
  }, []);

  const handleConfirm = useCallback(() => {
    setConfirmationModal(prev => {
      if (prev.onConfirm) {
        prev.onConfirm();
      }
      return { ...prev, isOpen: false, onConfirm: null, onCancel: null };
    });
  }, []);

  return {
    confirmationModal: {
      ...confirmationModal,
      onClose: closeConfirmation,
      onConfirm: handleConfirm
    },
    alertModal: {
      ...alertModal,
      onClose: closeAlert
    },
    showConfirmation,
    showAlert
  };
}

